<template>
  <div id="register-form">
    <VRow justify="center" wrap>
      <VCol cols="12" class="text-md-center">
        <div class="display-1 mb-2 mb-md-4">
          {{ $t('company') }}
        </div>
      </VCol>
      <VCol cols="12" sm="6" md="4" class="py-0">
        <VTextField
          id="organization"
          v-model="company.name"
          outlined
          dense
          type="text"
          data-cy="companyField"
          class="mb-2 mb-md-4"
          :error-messages="error.errors['company.name']"
          autocomplete="organization"
          @input="error.errors['company.name'] = null"
        >
          <template #label>
            {{ $t('companyName') }} <span class="red--text">*</span>
          </template>
        </VTextField>
      </VCol>
      <VCol cols="12" sm="6" md="4" class="py-0">
        <VAutocomplete
          v-model="company.countryId"
          dense
          outlined
          data-cy="countryField"
          :items="countries"
          item-value="id"
          item-text="name"
          :no-data-text="$t('noCountryFound')"
          autocomplete="disable-city"
          :error-messages="error.errors['company.countryId']"
          @change="error.errors['company.countryId'] = null"
        >
          <template v-slot:label>
            {{ $t('companyCountry') }} <span class="red--text">*</span>
          </template>
        </VAutocomplete>
      </VCol>
      <VCol cols="12" class="text-md-center">
        <div class="display-1 mb-2 mb-md-4">
          {{ $t('user') }}
        </div>
      </VCol>
      <VCol cols="12" sm="6" md="4" class="py-0 ">
        <VTextField
          id="firstName"
          v-model="user.firstName"
          data-cy="firstName"
          outlined
          dense
          type="text"
          class="mb-2 mb-md-4"
          :error-messages="error.errors['user.firstName']"
          autocomplete="given-name"
          @input="error.errors['user.firstName'] = null"
        >
          <template #label>
            {{ $t('firstName') }} <span class="red--text">*</span>
          </template>
        </VTextField>
        <VTextField
          id="lastName"
          v-model="user.lastName"
          data-cy="lastNameField"
          outlined
          dense
          type="text"
          class="mb-2 mb-md-4"
          :error-messages="error.errors['user.lastName']"
          autocomplete="family-name"
          @input="error.errors['user.lastName'] = null"
        >
          <template #label>
            {{ $t('lastName') }} <span class="red--text">*</span>
          </template>
        </VTextField>
      </VCol>
      <VCol cols="12" sm="6" md="4" class="py-0 ">
        <VTextField
          id="email"
          v-model="user.email"
          outlined
          data-cy="emailField"
          dense
          type="email"
          class="mb-2 mb-md-4"
          :error-messages="error.errors['user.email']"
          autocomplete="email"
          @input="error.errors['user.email'] = null"
        >
          <template #label>
            {{ $t('email') }} <span class="red--text">*</span>
          </template>
        </VTextField>
        <PhoneNumberInput
          v-model="user.phone"
          required
          class="mb-2 mb-md-4"
          :error-messages="error.errors['user.phone']"
          @input="error.errors['user.phone'] = null"
        />
      </VCol>
      <VCol cols="12" md="8" class="py-0">
        <VRow>
          <VCol cols="12" sm="6" md="6" class="py-0 ">
            <VTextField
              v-model="user.password"
              outlined
              dense
              data-cy="password"
              type="password"
              autocomplete="new-password"
              class="mb-2 mb-md-4"
              :error-messages="error.errors['user.password']"
              @input="error.errors['user.password'] = null"
            >
              <template #label>
                {{ $t('pass') }} <span class="red--text">*</span>
              </template>
            </VTextField>
          </VCol>
          <VCol cols="12" sm="6" md="6" class="py-0 ">
            <VTextField
              v-model="user.passwordConfirmation"
              outlined
              dense
              data-cy="confirmPassword"
              type="password"
              autocomplete="new-password"
              class="mb-2 mb-md-4"
              :error-messages="error.errors['user.passwordConfirmation']"
              @input="error.errors['user.passwordConfirmation'] = null"
            >
              <template #label>
                {{ $t('confirmPass') }} <span class="red--text">*</span>
              </template>
            </VTextField>
          </VCol>
        </VRow>
      </VCol>
      <VCol cols="12" md="8" class="text-center reg-form__check-box">
        <VCheckbox v-model="accept" data-cy="confirmCheckbox" class="d-flex justify-center">
          <span slot="label">
            {{ $t('agree') }}&nbsp;
            <RouterLink v-slot="{ href }" target="_blank" :to="{ name: 'confidential' }">
              <a :href="href" target="_blank" @click.stop>{{ $t('policy') }}</a>
            </RouterLink>
            <template v-if="locale === 'ru'">
              {{ $t('and') }}
              <RouterLink v-slot="{ href }" target="_blank" :to="{ name: 'agree' }">
                <a :href="href" target="_blank" @click.stop>{{ $t('userAgreement') }}</a>
              </RouterLink>
            </template>
          </span>
        </VCheckbox>
        <VBtn data-cy="signUpButton" :disabled="!accept" :loading="loading" @click="register">
          {{ $t('signUp') }}
        </VBtn>
      </VCol>
    </VRow>
  </div>
</template>

<script>
import { ref, reactive } from '@vue/composition-api';
import useUtm from '@/compositions/useUtm';
import { cabinetUrl, normalizePhoneNumber } from '@/functions';
import ApiMixin from '@/mixins/ApiMixin';
import RecaptchaMixin from '@/mixins/RecaptchaMixin';
import PhoneNumberInput from '@/inputs/PhoneNumberInput.vue';
import httpClient from '@/api';
import useLocale from '../../compositions/useLocale';

export default {
  name: 'LandingForm',
  components: {
    PhoneNumberInput,
  },
  mixins: [ApiMixin, RecaptchaMixin],
  setup() {
    const { getUtm } = useUtm();
    const { locale } = useLocale();

    const loginLink = cabinetUrl(`login?${new URLSearchParams(getUtm()).toString()}`);

    return {
      getUtm,
      loginLink,
      error: reactive({
        errorMessage: null,
        errors: {},
      }),
      company: reactive({
        name: null,
        countryId: null,
        invitedBy: null,
        test: false,
      }),
      user: reactive({
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        password: null,
        passwordConfirmation: null,
      }),
      accept: ref(false),
      loading: ref(false),
      countries: ref([]),
      locale: locale.value,
    };
  },
  created() {
    this.loadCountries();
    this.setQaData();
  },
  mounted() {
    httpClient.get('users/csrf-cookie');
  },
  methods: {
    async loadCountries() {
      this.countries = await this.$store.dispatch('country/fetch');
      const { locale } = useLocale();
      if (locale.value === 'en') {
        const usa = this.countries.find(country => country.alpha3Code === 'USA');
        if (usa.id) {
          this.company.countryId = usa.id;
        }
      }
    },

    async saveCredentials() {
      if (window.PasswordCredential) {
        try {
          await navigator.credentials.store(new PasswordCredential(
            { password: this.user.password, id: this.user.email },
          ));
        } catch (e) {
          console.warn(e);
        }
      }
    },

    async register() {
      this.withApiErrorHandling(async () => {
        const recaptchaToken = await this.getRecaptchaToken('companyRegistration');
        const utm = this.getUtm();
        await this.$store.dispatch('company/register', {
          company: {
            name: this.company.name,
            countryId: this.company.countryId,
            test: this.company.test,
            invitedBy: this.company.invitedBy
          },
          user: {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            phone: normalizePhoneNumber(this.user.phone),
            password: this.user.password,
            passwordConfirmation: this.user.passwordConfirmation,
          },
          utm: {
            campaign: utm.utm_campaign,
            content: utm.utm_content,
            medium: utm.utm_medium,
            source: utm.utm_source,
            term: utm.utm_term,
          },
          recaptchaToken,
        });

        await this.saveCredentials();

        if (window.dataLayer) {
          window.dataLayer.push({ event: 'reg_success' });
        }

        document.location.href = cabinetUrl();
      }, 'error', 'loading');
    },
    setQaData() {
      const { qamode, referralCode } = this.$route.query;
      if (qamode) {
        this.user.password = '12345678';
        this.user.passwordConfirmation = '12345678';
        this.user.email = '@huntica.works';
        this.company.test = true;
      }
      
      if (document.cookie.includes('referralCode')) {
        const cookie = document.cookie.split('; ').find(row => row.startsWith('referralCode='));
        this.company.invitedBy = cookie ? Number(cookie.split('=')[1]) : null;
      }
    },
  },
};
</script>
<style scoped>
.phone-number-input::v-deep .v-input__slot fieldset {
  border-radius: 4px;
}
</style>
<style lang="scss">
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .reg-form__check-box {
      .v-label {
        max-width: 300px;
        font-size: 14px;
        display: block !important;
      }
    }
  }
  @media (max-width: 385px) {
    .reg-form__check-box {
      .v-label {
        width: 160px;
      }
    }
  }
</style>

<i18n locale="en" lang="yaml">
  thanks: Thank you for registering!
  signIn: Sign in
  company: Company
  companyName: Company name
  companySite: Company website
  user: User
  firstName: First name
  lastName: Last name
  email: E-mail
  phone: Phone number
  pass: Password
  confirmPass: Verify password
  agree: I agree with
  policy: the privacy policy
  signUp: Sign up
  companyCountry: Country
  noCountryFound: Country not found
  and: and
  userAgreement: user agreement
</i18n>

<i18n locale="ru" lang="yaml">
  thanks: Спасибо за регистрацию!
  signIn: Войти на сайт
  company: Компания
  companyName: Название компании
  companySite: Сайт компании
  user: Пользователь
  firstName: Имя
  lastName: Фамилия
  email: E-mail
  phone: Телефон
  pass: Пароль
  confirmPass: Подтвердите пароль
  agree: Согласен с
  policy: политикой конфиденциальности
  signUp: Зарегистрироваться
  companyCountry: Страна
  noCountryFound: Страна не найдена
  and: и
  userAgreement: пользовательским соглашением
</i18n>
